import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Checkbox } from 'primereact/checkbox';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Messages } from 'primereact/messages';
import { ProgressBar } from 'primereact/progressbar';
import { SelectButton } from 'primereact/selectbutton';
import { Toast } from 'primereact/toast';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useAuth } from '../contexts/AuthContext.js';
import useStatusPageScript from '../hooks/useStatusPageScript.js';
import '../config.js';
import notificationsApi from '../api/notificationsApi.js';
import { canAccessNotificationSettings } from '../utils/accessControl.js';

const DEFAULT_SCHEDULE = Object.freeze({
	isEnabled: false,
	daysMask: 0,
	startMinute: 9 * 60,
	endMinute: 17 * 60,
});

const CLIENT_COOLDOWN_DEFAULT_ENABLED = true;
const CLIENT_COOLDOWN_SECONDS_FIXED = 90;
const CLIENT_COOLDOWN_LABEL_FIXED = '1 min 30 sec';

const DEFAULT_PREFERENCES = Object.freeze({
	clientOfflineUi: false,
	clientOfflineEmail: false,
	hubOfflineUi: false,
	hubOfflineEmail: false,
	clientOfflineFilterMode: 0,
	clientCooldownEnabled: CLIENT_COOLDOWN_DEFAULT_ENABLED,
	clientCooldownSeconds: CLIENT_COOLDOWN_SECONDS_FIXED,
});

const weekdayOptions = [
	{ label: 'Mon', value: 0 },
	{ label: 'Tue', value: 1 },
	{ label: 'Wed', value: 2 },
	{ label: 'Thu', value: 3 },
	{ label: 'Fri', value: 4 },
	{ label: 'Sat', value: 5 },
	{ label: 'Sun', value: 6 },
];

const clientOfflineFilterModeOptions = [
	{ label: 'All clients', value: 0 },
	{ label: 'Access clients only', value: 1 },
	{ label: 'Non-access clients only', value: 2 },
];

/** @param {number | null | undefined} minutes */
const minutesToHHmm = (minutes) => {
	if (minutes === null || minutes === undefined || Number.isNaN(Number(minutes))) return '';
	const clamped = Math.max(0, Math.min(1439, Number(minutes)));
	const hh = Math.floor(clamped / 60);
	const mm = clamped % 60;
	return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

/** @param {string} hhmm */
const hhmmToMinutes = (hhmm) => {
	const trimmed = String(hhmm || '').trim();
	const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(trimmed);
	if (!match) return null;
	const hours = Number(match[1]);
	const minutes = Number(match[2]);
	const total = hours * 60 + minutes;
	if (total < 0 || total > 1439) return null;
	return total;
};

/** @param {number} mask */
const daysMaskToSet = (mask) => {
	const normalized = Number(mask) || 0;
	/** @type {Set<number>} */
	const result = new Set();
	for (let bit = 0; bit < 7; bit += 1) {
		if ((normalized & (1 << bit)) !== 0) result.add(bit);
	}
	return result;
};

/** @param {Set<number>} set */
const setToDaysMask = (set) => {
	let mask = 0;
	for (const bit of set) {
		if (bit >= 0 && bit < 7) mask |= 1 << bit;
	}
	return mask;
};

const NotificationSettingsPage = () => {
	useStatusPageScript();

	const navigate = useNavigate();
	const { accessContext, isAuthReady, logout } = useAuth();
	const msgs = useRef(null);
	const toast = useRef(null);

	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [timeError, setTimeError] = useState(null);
	const [hasExistingSettings, setHasExistingSettings] = useState(false);

	const [schedule, setSchedule] = useState({ ...DEFAULT_SCHEDULE });
	const [preferences, setPreferences] = useState({ ...DEFAULT_PREFERENCES });

	const [locations, setLocations] = useState([]);
	const [locationFilter, setLocationFilter] = useState('');
	/** @type {[Set<number>, Function]} */
	const [selectedLocationIds, setSelectedLocationIds] = useState(new Set());

	const scheduleControlsDisabled = !Boolean(schedule.isEnabled);

	const handleAuthError = async () => {
		await logout();
		navigate('/login');
	};

	const progressbar = (
		<div className="card mt-3">
			<ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
		</div>
	);

	const filteredLocations = useMemo(() => {
		const q = locationFilter.trim().toLowerCase();
		const sorted = [...locations].sort((a, b) => String(a.name).localeCompare(String(b.name)));
		if (!q) return sorted;
		return sorted.filter((l) => String(l.name || '').toLowerCase().includes(q));
	}, [locations, locationFilter]);

	const selectedWeekdayValues = useMemo(() => {
		const set = daysMaskToSet(schedule.daysMask);
		return Array.from(set.values()).sort((a, b) => a - b);
	}, [schedule.daysMask]);

	const toggleLocation = (locationId, checked) => {
		setSelectedLocationIds((prev) => {
			const next = new Set(prev);
			if (checked) next.add(locationId);
			else next.delete(locationId);
			return next;
		});
	};

	const selectAllLocations = () => {
		setSelectedLocationIds(new Set(locations.map((l) => Number(l.id))));
	};

	const clearAllLocations = () => {
		setSelectedLocationIds(new Set());
	};

	const fetchLocations = async () => {
		const token = localStorage.getItem('token');
		const id = localStorage.getItem('id');
		const appid = localStorage.getItem('appid');

		// Existing repo pattern for a "locations list" in support views.
		if (!id) {
			return [];
		}

		let url = global.config.url + `/support/locationswithoutextras/${id}`;
		if (appid !== null && appid !== undefined && appid !== '') {
			url = url + `?appProviderId=${appid}`;
		}

		const response = await axios.get(url, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		});
		return Array.isArray(response.data) ? response.data : [];
	};

	const loadInitial = async () => {
		try {
			setLoading(true);
			setTimeError(null);
			if (msgs.current) msgs.current.clear();
            let id = localStorage.getItem('id');
            let appid = localStorage.getItem('appid');

			const [settingsResponse, locs] = await Promise.all([
				notificationsApi.getSettings(id, appid),
				fetchLocations(),
			]);


			//existing needs to happen if settingsResponse.schedule is not null 
			//or if settingsResponse.preferences.hubOfflineUi is true or if settingsResponse.preferences.clientOfflineUi is true 
			//or if settingsResponse.preferences.hubOfflineEmail is true or if settingsResponse.preferences.clientOfflineEmail is true
			//or if settingsResponse.locationIds is an array with length > 0
			const existing = Boolean(
				(settingsResponse.schedule && settingsResponse.schedule.isEnabled) ||
					(settingsResponse.preferences && (settingsResponse.preferences.hubOfflineUi || settingsResponse.preferences.clientOfflineUi || settingsResponse.preferences.hubOfflineEmail || settingsResponse.preferences.clientOfflineEmail)) ||
					(Array.isArray(settingsResponse.locationIds) && settingsResponse.locationIds.length > 0)
			);

			console.log(existing, settingsResponse);

			const safeSchedule = settingsResponse.schedule
				? {
						isEnabled: Boolean(settingsResponse.schedule.isEnabled),
						daysMask: Number(settingsResponse.schedule.daysMask) || 0,
						startMinute: Number(settingsResponse.schedule.startMinute) || 0,
						endMinute: Number(settingsResponse.schedule.endMinute) || 0,
					}
				: { ...DEFAULT_SCHEDULE };

			const safePreferences = settingsResponse.preferences
				? {
						clientOfflineUi: Boolean(settingsResponse.preferences.clientOfflineUi),
						clientOfflineEmail: Boolean(settingsResponse.preferences.clientOfflineEmail),
						hubOfflineUi: Boolean(settingsResponse.preferences.hubOfflineUi),
						hubOfflineEmail: Boolean(settingsResponse.preferences.hubOfflineEmail),
						clientOfflineFilterMode:
							Number.isFinite(Number(settingsResponse.preferences.clientOfflineFilterMode))
								? Number(settingsResponse.preferences.clientOfflineFilterMode)
								: 0,
						clientCooldownEnabled:
							typeof settingsResponse.preferences.clientCooldownEnabled === 'boolean'
								? settingsResponse.preferences.clientCooldownEnabled
								: CLIENT_COOLDOWN_DEFAULT_ENABLED,
						// Fixed for now (UI does not allow editing yet).
						clientCooldownSeconds: CLIENT_COOLDOWN_SECONDS_FIXED,
					}
				: { ...DEFAULT_PREFERENCES };

			const ids = new Set((settingsResponse.locationIds || []).map((x) => Number(x)));

			setSchedule(safeSchedule);
			setPreferences(safePreferences);
			setLocations(Array.isArray(locs) ? locs : []);
			setSelectedLocationIds(ids);
			setHasExistingSettings(existing);
		} catch (error) {
			console.error('Error loading notification settings:', error);
			if (error.response && (error.response.status === 401 || error.response.status === 403)) {
				await handleAuthError();
				return;
			}
			if (msgs.current) {
				msgs.current.show({
					severity: 'error',
					summary: '',
					detail: 'Failed to load notification settings. Please try again.',
					sticky: true,
				});
			}
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (!isAuthReady) return;

		if (!canAccessNotificationSettings(accessContext)) {
			navigate('/support/settings');
			return;
		}

		loadInitial();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accessContext, isAuthReady, navigate]);

	const validateTimeInputs = () => {
		if (schedule.startMinute < 0 || schedule.startMinute > 1439) return 'Start time must be within 00:00..23:59.';
		if (schedule.endMinute < 0 || schedule.endMinute > 1439) return 'End time must be within 00:00..23:59.';
		return null;
	};

	const getDisabledNotificationsWarning = () => {
		if (!preferences.clientOfflineEmail && !preferences.hubOfflineEmail) {
			return (
				'Both Hub/Relay and Client email notifications are disabled. ' +
				'If you save now, you will not receive any notification emails.\n\n' +
				'Do you want to continue?'
			);
		}
		return null;
	};

	const validateClientOfflineFilterMode = () => {
		const mode = Number(preferences.clientOfflineFilterMode);
		if (mode !== 0 && mode !== 1 && mode !== 2) return 'Client notifications filter must be one of: All, Access only, Non-access only.';
		return null;
	};

	const onSave = async () => {
        let id = localStorage.getItem('id');
        let appid = localStorage.getItem('appid');
		const err = validateTimeInputs();
		setTimeError(err);
		if (err) {
			if (toast.current) {
				toast.current.show({ severity: 'error', summary: '', detail: err });
			}
			return;
		}

		const disabledWarn = getDisabledNotificationsWarning();
		if (disabledWarn) {
			// Keep UX minimal: warn + ask to continue.
			// eslint-disable-next-line no-alert
			const shouldContinue = window.confirm(disabledWarn);
			if (!shouldContinue) return;
		}

		const filterErr = validateClientOfflineFilterMode();
		if (filterErr) {
			if (toast.current) {
				toast.current.show({ severity: 'error', summary: '', detail: filterErr });
			}
			return;
		}

		try {
			setSaving(true);
			if (msgs.current) msgs.current.clear();

			/** @type {import('../api/types/notifications').NotificationSettingsRequest} */
			const payload = {
				schedule: {
					isEnabled: Boolean(schedule.isEnabled),
					daysMask: Number(schedule.daysMask) || 0,
					startMinute: Number(schedule.startMinute) || 0,
					endMinute: Number(schedule.endMinute) || 0,
				},
				locationIds: Array.from(selectedLocationIds.values()).map((x) => Number(x)),
				preferences: {
					clientOfflineUi: Boolean(preferences.clientOfflineUi),
					clientOfflineEmail: Boolean(preferences.clientOfflineEmail),
					hubOfflineUi: Boolean(preferences.hubOfflineUi),
					hubOfflineEmail: Boolean(preferences.hubOfflineEmail),
					clientOfflineFilterMode: Number(preferences.clientOfflineFilterMode) || 0,
					clientCooldownEnabled: Boolean(preferences.clientCooldownEnabled),
					clientCooldownSeconds: CLIENT_COOLDOWN_SECONDS_FIXED,
				},
			};

			await notificationsApi.updateSettings(id, payload, appid);

			if (toast.current) {
				toast.current.show({
					severity: 'success',
					summary: '',
					detail: hasExistingSettings ? 'Notification settings updated.' : 'Notification settings saved.',
				});	
			}
			setHasExistingSettings(true);
		} catch (error) {
			console.error('Error saving notification settings:', error);
			if (error.response && (error.response.status === 401 || error.response.status === 403)) {
				await handleAuthError();
				return;
			}
			const detail =
				(error.response && error.response.data) ||
				error.message ||
				'Failed to save notification settings.';
			if (msgs.current) {
				msgs.current.show({ severity: 'error', summary: '', detail, sticky: true });
			}
			if (toast.current) {
				toast.current.show({ severity: 'error', summary: '', detail: 'Save failed. Please try again.' });
			}
		} finally {
			setSaving(false);
		}
	};

	return (
		<Card className="justify-content-md-center">
			<Container fluid>
				<div className="panel">
					<div className="flex align-items-center justify-content-between flex-wrap" style={{ gap: '0.75rem' }}>
						<div className="flex align-content-center flex-wrap">
							<Button link icon="pi pi-arrow-left" onClick={() => navigate(-1)} className="flex" />
							<h4 className="flex ml-3 mt-2">Notification Settings</h4>
						</div>
						<Button
							label="Save"
							icon="pi pi-save"
							className="p-button-success"
							onClick={onSave}
							loading={saving}
							disabled={loading || saving}
						/>
					</div>
				</div>

				<Messages ref={msgs} />
				<Toast ref={toast} position="top-center" />

				{loading && progressbar}

				{!loading && (
					<Row>
					<Col md={6} className="">
						{/* <div className="mt-3"> */}
							<Card title="Preferences">
							{/* <div className="flex align-items-center justify-content-between mb-3">
								<span>Client offline: UI popup</span>
								<InputSwitch
									checked={Boolean(preferences.clientOfflineUi)}
									onChange={(e) =>
										setPreferences((prev) => ({ ...prev, clientOfflineUi: Boolean(e.value) }))
									}
								/>
							</div> */}
							<div className="flex align-items-center justify-content-between mb-3">
								<span>Hub & Relay Status Notifications: Email</span>
								<InputSwitch
									checked={Boolean(preferences.hubOfflineEmail)}
									onChange={(e) =>
										setPreferences((prev) => ({ ...prev, hubOfflineEmail: Boolean(e.value) }))
									}
								/>
							</div>
							<hr className="my-3" />
							<div className="">
								<div className="mb-2" style={{ fontWeight: 600 }}>
									Client Notifications
								</div>
								<div className="flex align-items-center justify-content-between mb-3">
									<span>LMS Client Status Notifications: Email</span>
									<InputSwitch
										checked={Boolean(preferences.clientOfflineEmail)}
										onChange={(e) =>
											setPreferences((prev) => ({ ...prev, clientOfflineEmail: Boolean(e.value) }))
										}
									/>
								</div>

								<div className="mb-3" style={{ paddingLeft: '1rem' }}>
									<div className="flex align-items-center justify-content-between mb-2">
										<span>Ignore brief LMS Client disconnects <small className="text-muted">(Delay: {CLIENT_COOLDOWN_LABEL_FIXED})</small></span>
										<InputSwitch
											checked={Boolean(preferences.clientCooldownEnabled)}
											onChange={(e) =>
												setPreferences((prev) => ({
													...prev,
													clientCooldownEnabled: Boolean(e.value),
													clientCooldownSeconds: CLIENT_COOLDOWN_SECONDS_FIXED,
												}))
										}
										/>
									</div>
									{/* <div style={!preferences.clientCooldownEnabled ? { opacity: 0.5 } : undefined}>
										
									</div> */}
									<div className="mt-2">
										<small className="text-muted">
											{preferences.clientCooldownEnabled
												? `If an LMS Client briefly disconnects and comes back online within ${CLIENT_COOLDOWN_LABEL_FIXED}, you will not get an offline or online notification.`
												: 'Notifications will be sent immediately when an LMS Client goes offline or comes back online. Short network drops may create extra notifications.'}
										</small>
									</div>
								</div>

								<div className="mb-2" style={{ paddingLeft: '1rem' }}>
									LMS Client notifications filter
								</div>
								<div className="" style={{ paddingLeft: '1rem' }}>
									<SelectButton
										value={Number(preferences.clientOfflineFilterMode) || 0}
										options={clientOfflineFilterModeOptions}
										onChange={(e) =>
											setPreferences((prev) => ({ ...prev, clientOfflineFilterMode: Number(e.value) }))
										}
									/>
								</div>
							</div>
							{/* <div className="flex align-items-center justify-content-between mb-3">
								<span>Hub offline: UI popup</span>
								<InputSwitch
									checked={Boolean(preferences.hubOfflineUi)}
									onChange={(e) => setPreferences((prev) => ({ ...prev, hubOfflineUi: Boolean(e.value) }))}
								/>
							</div> */}
							</Card>
						{/* </div> */}
						<Card title="Time Setting for Receiving Emails of Live Notifications (London time)" >
							<div className="flex align-items-center">
								<span className="mr-3">Enable Email Schedule</span>

								<InputSwitch
									checked={Boolean(schedule.isEnabled)}
									onChange={(e) => setSchedule((prev) => ({ ...prev, isEnabled: Boolean(e.value) }))}
								/>

							</div>
							<div>
								<small className="text-muted">
									If not enabled, you will receive emails for all notifications at all times.
								</small>
							</div>

									<div className="mt-4" style={scheduleControlsDisabled ? { opacity: 0.5, pointerEvents: 'none' } : undefined}>
								{/* <div className="mb-2">Days</div> */}
								<SelectButton
									value={selectedWeekdayValues}
									options={weekdayOptions}
									multiple
											disabled={scheduleControlsDisabled}
									onChange={(e) => {
										const nextSet = new Set(Array.isArray(e.value) ? e.value : []);
										setSchedule((prev) => ({ ...prev, daysMask: setToDaysMask(nextSet) }));
									}}
								/>
							</div>

									<div className="mt-2" style={scheduleControlsDisabled ? { opacity: 0.5 } : undefined}>
								<Row>
									<Col sm={6} className="">
										<label className="mb-2" style={{ display: 'block' }}>
											Start
										</label>
										<InputText
											type="time"
											value={minutesToHHmm(schedule.startMinute)}
													disabled={scheduleControlsDisabled}
											onChange={(e) => {
												const minutes = hhmmToMinutes(e.target.value);
												if (minutes === null) {
													setTimeError('Please enter a valid start time (HH:mm).');
													return;
												}
												setTimeError(null);
												setSchedule((prev) => ({ ...prev, startMinute: minutes }));
											}}
										/>
									</Col>
									<Col sm={6} className="">
										<label className="mb-2" style={{ display: 'block' }}>
											End
										</label>
										<InputText
											type="time"
											value={minutesToHHmm(schedule.endMinute)}
													disabled={scheduleControlsDisabled}
											onChange={(e) => {
												const minutes = hhmmToMinutes(e.target.value);
												if (minutes === null) {
													setTimeError('Please enter a valid end time (HH:mm).');
													return;
												}
												setTimeError(null);
												setSchedule((prev) => ({ ...prev, endMinute: minutes }));
											}}
										/>
									</Col>
								</Row>
								{timeError && <small className="p-error">{timeError}</small>}
								<div className="">
									<small className="text-muted">
										Overnight ranges are allowed.
									</small>
								</div>
							</div>
						</Card>

						
					</Col>

					<Col md={6} className="mb-3">
						<Card
							title="Locations"
							subTitle={`Subscribed to ${selectedLocationIds.size} of ${locations.length} locations.`}
						>
							<div className="flex align-items-center flex-wrap">
								<span className="p-input-icon-left mr-3 mb-2">
									<i className="pi pi-search" />
									<InputText
										value={locationFilter}
										onChange={(e) => setLocationFilter(e.target.value)}
										placeholder="Search locations"
									/>
								</span>
								<Button
									label="Select all"
									className="p-button-text mb-2"
									onClick={selectAllLocations}
									disabled={locations.length === 0}
								/>
								<Button
									label="Clear all"
									className="p-button-text mb-2"
									onClick={clearAllLocations}
									disabled={selectedLocationIds.size === 0}
								/>
							</div>

							<div className="mt-3" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
								{filteredLocations.length === 0 && (
									<div className="text-muted">No locations found.</div>
								)}

								{filteredLocations.map((loc) => {
									const id = Number(loc.id);
									const checked = selectedLocationIds.has(id);
									const inputId = `notif-loc-${id}`;
									return (
										<div key={id} className="flex align-items-center mb-2">
											<Checkbox
												inputId={inputId}
												checked={checked}
												onChange={(e) => toggleLocation(id, Boolean(e.checked))}
											/>
											<label htmlFor={inputId} className="ml-2">
												{loc.name}
											</label>
										</div>
									);
								})}
							</div>
						</Card>
					</Col>
					</Row>
				)}
			</Container>
		</Card>
	);
};

export default NotificationSettingsPage;
